import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { Navigation2Service } from '../../../services/navigation2.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { PriceService } from 'src/app/services/price/price.service';
import { SelectedAttributeService } from '../../../services/selected-attribute.service';
import { CheckoutService } from '../../../services/checkout.service';
import { MatDialog } from '@angular/material/dialog';
import { ResetDialogComponent } from '../../reset.dialog/reset.dialog.component';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { BookmarksService } from '../../../services/bookmarks.service';
import { assert } from 'src/app/utils/assert.util';
import { MainImageService } from 'src/app/services/main-image/main-image.service';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { CheckoutSummaryComponent } from '../../checkout-summary/checkout-summary.component';
import { CustomerThemeService } from 'src/app/services/customer-theme/customer-theme.service';
import { ThumbnailProviderService } from 'src/app/providers/thumbnail/thumbnail.provider.service';

@Component({
  selector: 'app-bottom-bar',
  standalone: false,
  templateUrl: './bottom-bar.component.html',
  styleUrl: './bottom-bar.component.scss'
})
export class BottomBarComponent {
  private iframeService = inject(IframeService);
  private customerThemeService = inject(CustomerThemeService);
  logo = computed(() => this.customerThemeService.theme()?.logo ?? null);
  private navigationService = inject(Navigation2Service);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private priceService = inject(PriceService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private checkoutService = inject(CheckoutService);
  private loadingService = inject(LoadingService);
  private dialog = inject(MatDialog);
  private configuredItemService = inject(ConfiguredItemService);
  private bookmarksService = inject(BookmarksService);
  private mainImageService = inject(MainImageService);
  private thumbnailProvider = inject(ThumbnailProviderService);
  canRetailerAddToCart = input(false);
  b2bDataSubscription: Subscription | null = null;
  skuToShow = signal("");
  wholesalePriceToShow = computed(() => this.priceService.wholesalePriceToShow());
  retailPrice = computed(() =>  this.priceService.retailPrice());
  shouldShowWholesalePrice = signal<boolean>(false);
  canUndo = computed(() => this.selectedAttributeService.undoList().length > 0);
  canRedo = computed(() => this.selectedAttributeService.redoList().length > 0);
  isRetailerMode = computed(() => this.checkoutService.isRetailerOnly());
  shouldShowPrice = computed(() => this.navigationService.activeView().title !== 'Retailer Summary');
  customSKU = computed(() => this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
  bookmarkKey = computed(() => {
    const sku = this.customSKU();
    const haloSKU = !this.configuredItemService.isCustomSKU(this.skuToShow()) && !this.configuredItemService.isCustomStyle(this.skuToShow()) ? this.skuToShow() : '';
    return `${sku}:${haloSKU}`;
  });
  bookmarks = computed(() => this.bookmarksService.bookmarks());
  isReviewing = signal(false);
  isRetailOnlyMode = computed(() => this.checkoutService.isRetailerOnly());
  /** Guards against a second add-to-cart while the first is waiting on the thumbnail (ADO #8902). */
  private isAddingToCart = false;

  constructor() {
    effect(async () => {
      this.loadingService.load('previewer::price');
      const pricingObject = this.selectedAttributeService.pricingObject();
      if (pricingObject) {
        await this.priceService.update(pricingObject, untracked(() => this.selectedAttributeService.configuredItem()));
      }
      this.loadingService.done('previewer::price');
    }, { allowSignalWrites: true });

    this.navigationService.subscribe((action) => {
      if(action.data.title === 'Review') {
        if (action.action === 'Present') {
          this.isReviewing.set(true);
        } else if (action.action === 'Dismiss') {
          this.isReviewing.set(false);
        }
      }
    });

    // this.navigationService.subscribe((action) => {
    //   if (action.action === 'Dismiss' && action.data?.title === this.tabItems()[1].view?.title) {
    //     this.activeTab.set(this.tabItems()[0]);
    //     this.navigationService.overrideTitle.set(null);
    //     this.bookmarksService.skusToCompare.set([]);
    //   }
    // });
  }

  ngOnInit() {
    const size = this.selectedAttributeService.getValue().size;
    assert(size, 'No size selected');
    this.shouldShowWholesalePrice.set(!this.checkoutService.b2bData.retailPricingOnly);
    this.skuToShow.set((!this.checkoutService.b2bData.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(this.checkoutService.b2bData.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
    this.b2bDataSubscription = this.checkoutService.subscribeToB2BDataChanges(async value => {
      const size = this.selectedAttributeService.getValue().size;
      assert(size, 'No size selected');
      this.loadingService.load('SKUToShow');
      this.skuToShow.set((!value.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(value.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
      this.loadingService.done('SKUToShow');
    });
  }

  reset() {
    this.triggerGAEvent('click', 'reset', '1');
    this.dialog.open(ResetDialogComponent, { disableClose: true }).afterClosed().subscribe((v) => {
      if (v) {
        this.selectedAttributeService.reset();
      }
    })
  }

  async undo() {
    this.triggerGAEvent('click', 'undo', '1');
    if (this.canUndo()) {
      await this.selectedAttributeService.undo();
    }
  }

  async redo() {
    this.triggerGAEvent('click', 'redo', '1');
    if (this.canRedo()) {
      await this.selectedAttributeService.redo();
    }
  }

  async toggleBookmark() {
    this.loadingService.load('bookmarks::toggle');
    if (this.bookmarks().some(b => b.sku === this.bookmarkKey())) {
      await this.bookmarksService.remove(this.bookmarkKey());
    } else {
      const snapshot = await this.mainImageService?.getSnapshot?.call(this.mainImageService);
      assert(snapshot, 'Could not get bookmark thumbnail');
      await this.bookmarksService.add(this.bookmarkKey(), snapshot);
    }
    this.loadingService.done('bookmarks::toggle');
  }

  isBookmarkKeyInBookmarks(bookmarkKey: string) {
    // console.log(this.bookmarks(), bookmarkKey)
    return this.bookmarks().some(b => b.sku === bookmarkKey);
  }

  async reviewClicked() {
    if (!this.isReviewing()) {
      this.summaryClick()
    } else if (this.isRetailOnlyMode()) {
      // Waiting here, rather than relying on the loading overlay, is deliberate: the overlay is not
      // a reliable gate (on mobile app-toolbar sits above it at z-index 6), and awaiting is the only
      // thing that guarantees the retailer's cart event follows the image (ADO #8902).
      if (this.isAddingToCart) {
        return;
      }
      this.isAddingToCart = true;
      this.loadingService.load('bottom-bar:add-to-cart');
      try {
        await this.thumbnailProvider.whenSubmissionSettled();
        const engravingText = this.selectedAttributeService.configuredItem().engravingText;
        const engravingType = this.selectedAttributeService.configuredItem().engravingType
        this.iframeService.sendMessage(JSON.stringify({
          type: 'Benchmark::RingLab::addtocart',
          value: {
            sku: this.skuToShow(),
            engravingType: engravingType && engravingText ? engravingType.displayName : null,
            engravingText: engravingType && engravingText ? engravingText : null,
          }
        }));
      } finally {
        this.loadingService.done('bottom-bar:add-to-cart');
        this.isAddingToCart = false;
      }
    } else {
      this.addToCartClick();
    }
  }

  summaryClick() {
    this.triggerGAEvent('click', 'menu_item', 'Review');
    this.navigationService.present({ title: 'Review', type: CheckoutSummaryComponent, doesAppearInProgressBar: true });
  }

  async addToCartClick() {
    try {
      this.loadingService.load('checkout-summary:add-to-cart');
      const pricingObject = this.selectedAttributeService.pricingObject();
      assert(pricingObject, 'Pricing object was null');
      await this.priceService.update(pricingObject, this.selectedAttributeService.configuredItem());
      const priceToSend = this.priceService.wholesalePrice();
      assert(priceToSend, 'No price');
      const b2bData = await this.checkoutService.getOutputData(this.selectedAttributeService.configuredItem(), this.selectedAttributeService.poNumber(), this.priceService.isPricedItemQuarterSized());
      const roundedPrice = Math.round((priceToSend.valueBeforeFee + Number.EPSILON) * 100) / 100
      if (b2bData.unitPrice !== roundedPrice) {
        b2bData.unitPrice = roundedPrice;
        console.log('price updated')
      }

      const styleNumber = this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem());

      if (this.googleAnalyticsService.isInitialized) {
        this.googleAnalyticsService.triggerEvent('click', 'Review', 'add_to_cart', `${styleNumber} sz: ${this.selectedAttributeService.configuredItem().size?.value ?? 'None'}`);
      }
      const payload = JSON.stringify(b2bData);
      const encodedPayload = btoa(payload);
      // Navigating away can abort an in-flight thumbnail upload outright, not merely race it, so
      // wait for it to settle before leaving the page (ADO #8902).
      await this.thumbnailProvider.whenSubmissionSettled();
      window.location.href = `${environment.b2bURL}/customitem?o=${encodedPayload}`;
    } catch (e) {
      console.error(e);
      this.loadingService.done('checkout-summary:add-to-cart');
    }
  }

  isConfiguredItemComplete() {
    return Object.keys(this.selectedAttributeService.configuredItem()).filter(k => !k.toLowerCase().includes('gemstone') && !k.includes('engraving') && !k.includes('design')).every(k => (this.selectedAttributeService.configuredItem() as any)[k]);
  }

  triggerGAEvent(action: string, title: string, value: string) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'bottom_bar', title, value);
    }
  }
}
